import React, { useState, useMemo, useCallback } from 'react'
import {
  Box,
  Button,
  Flex,
  Spinner,
  Center,
  useToast,
  Icon,
  HStack,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FaArrowLeft,
  FaSync,
  FaStar,
  FaRegStar,
  FaTrash,
  FaEdit,
} from 'react-icons/fa'

import DataEntryTable from '../components/DataEntryTable'
import AddEntry from '../components/AddEntry'
import DeleteDataModal from '../components/DeleteDataModal'
import DeleteRecordConfirmDialog from '../components/DeleteRecordConfirmDialog'
import { ExchangeSymbolCascader } from '../components/ExchangeSymbolCascader'
import SymbolData from '../constants/SymbolData'
import { dataService, subscriptionService } from '../services/api'
import useAuth from '../hooks/useAuth'
import type { DataEntriesWithoutEntriesResponse, EntryRecord } from '../types'

const DataDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const recordId = Number(id)
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { userId } = useAuth()
  const queryClient = useQueryClient()
  const pageBgColor = useColorModeValue('white', 'gray.800')
  const pageBorderColor = useColorModeValue('gray.200', 'gray.700')

  const [isSubscribing, setIsSubscribing] = useState(false)
  const [isUnsubscribing, setIsUnsubscribing] = useState(false)
  const [isUpdatingSymbols, setIsUpdatingSymbols] = useState(false)
  const [editingSymbols, setEditingSymbols] = useState<string[]>([])
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure()
  const [isDeletingRecord, setIsDeletingRecord] = useState(false)
  const {
    isOpen: isDeleteRecordConfirmOpen,
    onOpen: onDeleteRecordConfirmOpen,
    onClose: onDeleteRecordConfirmClose,
  } = useDisclosure()
  const {
    isOpen: isSymbolEditorOpen,
    onOpen: onSymbolEditorOpen,
    onClose: onSymbolEditorClose,
  } = useDisclosure()

  const groupedSymbolData = useMemo(() => {
    const map: Record<string, string[]> = {}
    SymbolData.forEach(item => {
      const parts = item.split('|')
      if (parts.length !== 2) return
      const [exchange, symbol] = parts
      if (!map[exchange]) {
        map[exchange] = []
      }
      map[exchange].push(symbol)
    })
    return map
  }, [])

  // Query for fetching data entries
  const { data, isLoading, error, refetch } = useQuery<{
    record: EntryRecord
    user: DataEntriesWithoutEntriesResponse['data']['user']
    features_original?: DataEntriesWithoutEntriesResponse['data']['features_original']
    features_sorted?: DataEntriesWithoutEntriesResponse['data']['features_sorted']
    target_symbols?: DataEntriesWithoutEntriesResponse['data']['target_symbols']
  }>({
    queryKey: ['dataEntries', recordId],
    queryFn: async () => {
      if (!recordId || isNaN(recordId)) {
        throw new Error('Invalid record ID')
      }

      const base = await dataService.getDataEntriesWithoutEntries(recordId)

      const baseRecord = base.data.record
      if (!baseRecord) {
        throw new Error('Record not found')
      }

      return {
        record: {
          ...baseRecord,
          symbols: base.data.symbols,
          target_symbols: base.data.target_symbols,
        },
        user: base.data.user,
        features_original: base.data.features_original,
        features_sorted: base.data.features_sorted,
        target_symbols: base.data.target_symbols,
      }
    },
    enabled: !!recordId && !isNaN(recordId),
  })

  // Query for getting all user subscriptions
  const { data: subscriptionData, refetch: refetchSubscription } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      return await subscriptionService.getAllSubscriptions()
    },
    enabled: !!userId,
  })

  const isOwner = useMemo(() => {
    return !!userId && !!data?.record?.user_id && userId === data.record.user_id
  }, [userId, data?.record?.user_id])

  const isSubscribed = useMemo(() => {
    if (!userId || !recordId || subscriptionData === undefined) return null
    if (!subscriptionData.data?.subscriptions) return null
    return subscriptionData.data.subscriptions.some(
      (sub) => sub.record_id === recordId && sub.subscription.is_active
    )
  }, [userId, recordId, subscriptionData])

  const handleRefresh = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['entries', recordId] })
    toast({
      title: 'Refreshing data',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleBack = () => {
    // Check if we can go back in history
    if (location.key !== 'default') {
      navigate(-1) // Navigate to previous page in history
    } else {
      // If there's no history (e.g., opened in new tab), go to the records page
      navigate('/data')
    }
  }

  const handleSubscribe = async () => {
    if (!recordId) return

    setIsSubscribing(true)
    try {
      const response = await subscriptionService.subscribe(recordId)

      toast({
        title: 'Subscribed successfully',
        description: `You now have access until ${new Date(response.data.expires_at).toLocaleDateString()}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      refetchSubscription()
    } catch (error) {
      toast({
        title: 'Subscription failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSubscribing(false)
    }
  }

  const handleUnsubscribe = async () => {
    if (!recordId) return

    setIsUnsubscribing(true)
    try {
      const response = await subscriptionService.cancelSubscription(recordId)

      toast({
        title: 'Unsubscribed successfully',
        description: response.message,
        status: 'info',
        duration: 5000,
        isClosable: true,
      })

      refetchSubscription()
    } catch (error) {
      toast({
        title: 'Failed to unsubscribe',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsUnsubscribing(false)
    }
  }

  const handleDeleteRecord = async () => {
    if (!recordId || !isOwner) return

    setIsDeletingRecord(true)
    try {
      const response = await dataService.deleteRecord(recordId)
      toast({
        title: 'Record deleted',
        description: response.message || 'The record has been deleted successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })

      onDeleteRecordConfirmClose()
      if (location.key !== 'default') {
        navigate(-1)
      } else {
        navigate('/data')
      }
    } catch (error) {
      toast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsDeletingRecord(false)
    }
  }

  const handleOpenSymbolEditor = useCallback(() => {
    setEditingSymbols(data?.record?.symbols || [])
    onSymbolEditorOpen()
  }, [data?.record?.symbols, onSymbolEditorOpen])

  const handleCancelSymbolEditor = useCallback(() => {
    setEditingSymbols(data?.record?.symbols || [])
    onSymbolEditorClose()
  }, [data?.record?.symbols, onSymbolEditorClose])

  const handleSaveSymbols = useCallback(async () => {
    if (!recordId || !isOwner) return

    const nextSymbols = Array.from(new Set(editingSymbols.map((s) => s.trim()).filter(Boolean)))
    if (nextSymbols.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please keep at least one symbol.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsUpdatingSymbols(true)
    try {
      const response = await dataService.updateRecordSymbols(recordId, nextSymbols)
      toast({
        title: 'Symbols updated',
        description: response.message || 'Record symbols were updated successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['entries', recordId] })
      onSymbolEditorClose()
    } catch (error) {
      toast({
        title: 'Update failed',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsUpdatingSymbols(false)
    }
  }, [recordId, isOwner, editingSymbols, toast, refetch, queryClient, onSymbolEditorClose])

  return (
    <Box p={4}>
      <Flex
        mb={8}
        align="center"
        justifyContent="space-between"
        bg={pageBgColor}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={pageBorderColor}
      >
        <Button
          leftIcon={<Icon as={FaArrowLeft} />}
          onClick={handleBack}
          variant="ghost"
          size="md"
          _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
          fontWeight="medium"
          px={4}
        >
          Back to Records
        </Button>

        <HStack spacing={4} alignItems="center">
          {isOwner && (
            <>
              <AddEntry recordId={recordId} onSuccess={refetch} />
              <Button
                leftIcon={<Icon as={FaEdit} />}
                colorScheme="cyan"
                variant="outline"
                onClick={handleOpenSymbolEditor}
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
              >
                Update Symbols
              </Button>
              <Button
                leftIcon={<Icon as={FaTrash} />}
                colorScheme="red"
                onClick={onDeleteModalOpen}
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
              >
                Delete Data
              </Button>
              <Button
                leftIcon={<Icon as={FaTrash} />}
                colorScheme="red"
                variant="outline"
                onClick={onDeleteRecordConfirmOpen}
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
              >
                Delete Record
              </Button>
            </>
          )}
          {!isOwner &&
            isSubscribed !== null &&
            (isSubscribed ? (
              <Button
                leftIcon={<Icon as={FaStar} />}
                colorScheme="yellow"
                onClick={handleUnsubscribe}
                isLoading={isUnsubscribing}
                loadingText="Cancelling"
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
              >
                Cancel Subscription
              </Button>
            ) : (
              <Button
                leftIcon={<Icon as={FaRegStar} />}
                colorScheme="purple"
                onClick={handleSubscribe}
                isLoading={isSubscribing}
                loadingText="Subscribing"
                size="md"
                fontWeight="semibold"
                boxShadow="sm"
              >
                Subscribe
              </Button>
            ))}

          <Button
            leftIcon={<Icon as={FaSync} />}
            onClick={handleRefresh}
            isLoading={isLoading}
            variant="outline"
            colorScheme="green"
            size="md"
            fontWeight="medium"
            _hover={{
              bg: useColorModeValue('green.50', 'green.900'),
            }}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {isOwner && (
        <Modal isOpen={isSymbolEditorOpen} onClose={handleCancelSymbolEditor} size="3xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Symbols</ModalHeader>
            <ModalCloseButton isDisabled={isUpdatingSymbols} />
            <ModalBody>
              <ExchangeSymbolCascader
                groupedData={groupedSymbolData}
                selectedValues={editingSymbols}
                onChange={setEditingSymbols}
                placeholder="Add or select symbols"
                isMulti={true}
              />
            </ModalBody>
            <ModalFooter>
              <HStack spacing={2}>
                <Button
                  variant="ghost"
                  onClick={handleCancelSymbolEditor}
                  isDisabled={isUpdatingSymbols}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSaveSymbols}
                  isLoading={isUpdatingSymbols}
                  loadingText="Saving"
                >
                  Save
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Delete data under this record */}
      {isOwner && (
        <DeleteDataModal
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
          recordId={recordId}
          symbols={data?.record?.symbols || []}
          onSuccess={refetch}
        />
      )}

      {/* Delete record confirm dialog */}
      {isOwner && (
        <DeleteRecordConfirmDialog
          isOpen={isDeleteRecordConfirmOpen}
          onClose={onDeleteRecordConfirmClose}
          onConfirm={handleDeleteRecord}
          isLoading={isDeletingRecord}
        />
      )}

      {isLoading && !data ? (
        <Center h="60vh">
          <Spinner size="xl" color="blue.500" />
        </Center>
      ) : (
        <DataEntryTable
          recordId={recordId}
          data={data ?? null}
          isLoading={isLoading}
          error={error}
        />
      )}
    </Box>
  )
}

export default DataDetailPage
